import React, { useEffect, useState } from 'react';
import { CategoriaCircularizacao, ITemplateCircularizacao, TipoTemplateCircularizacao } from '../interfaces/ICircularizacao';
import { CircularizacaoService } from '../services/CircularizacaoService';

interface Props {
  categoria: CategoriaCircularizacao;
  onFechar: () => void;
}

const TIPOS: { value: TipoTemplateCircularizacao; label: string }[] = [
  { value: 'INICIAL', label: 'Carta Inicial' },
  { value: 'LEMBRETE_D2', label: 'Lembrete (D-2)' },
  { value: 'COBRANCA_D1', label: 'Cobrança (D+1)' },
];

const LABEL_ESCOPO: Record<ITemplateCircularizacao['escopo'], string> = {
  INSTITUCIONAL: 'Institucional',
  PESSOAL: 'Pessoal',
  COMPARTILHADO: 'Compartilhado',
};

// Card 07 do Épico Backend — 45 templates institucionais (semeados no Back-End) + modelos
// pessoais/compartilhados. Institucional nunca é editado diretamente (RN-014) — só duplicado para
// um modelo próprio, que aí sim pode ser editado.
const TemplatesModal: React.FC<Props> = ({ categoria, onFechar }) => {
  const [tipo, setTipo] = useState<TipoTemplateCircularizacao>('INICIAL');
  const [templates, setTemplates] = useState<ITemplateCircularizacao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [semeando, setSemeando] = useState(false);
  const [duplicandoId, setDuplicandoId] = useState<string | undefined>();
  const [nomeDuplicata, setNomeDuplicata] = useState('');
  const [templateEmEdicao, setTemplateEmEdicao] = useState<ITemplateCircularizacao | undefined>();
  const [assuntoEdicao, setAssuntoEdicao] = useState('');
  const [corpoEdicao, setCorpoEdicao] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState('');

  const carregar = async () => {
    setCarregando(true);
    const lista = await CircularizacaoService.listarTemplates(categoria, tipo);
    setTemplates(lista);
    setCarregando(false);
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoria, tipo]);

  const semearInstitucionais = async () => {
    setSemeando(true);
    await CircularizacaoService.semearTemplatesInstitucionais();
    setSemeando(false);
    await carregar();
  };

  const confirmarDuplicar = async (template: ITemplateCircularizacao) => {
    if (!nomeDuplicata.trim()) return;
    await CircularizacaoService.duplicarTemplate(template.id, nomeDuplicata.trim());
    setDuplicandoId(undefined);
    setNomeDuplicata('');
    setMensagem('Modelo duplicado — já pode ser personalizado.');
    await carregar();
  };

  const abrirEdicao = (template: ITemplateCircularizacao) => {
    setTemplateEmEdicao(template);
    setAssuntoEdicao(template.versaoVigente?.assunto ?? '');
    setCorpoEdicao(template.versaoVigente?.corpoHtml ?? '');
  };

  const salvarEdicao = async () => {
    if (!templateEmEdicao) return;
    setSalvando(true);
    await CircularizacaoService.atualizarTemplate(templateEmEdicao.id, assuntoEdicao, corpoEdicao);
    setSalvando(false);
    setTemplateEmEdicao(undefined);
    setMensagem('Modelo atualizado.');
    await carregar();
  };

  return (
    <div className='circ-modal-backdrop' onMouseDown={(e) => e.target === e.currentTarget && onFechar()}>
      <div className='circ-modal circ-modal-lg'>
        <div className='circ-modal-header'>
          <h4>Modelos de Carta</h4>
          <button type='button' className='btn btn-sm btn-icon btn-light' aria-label='Fechar' onClick={onFechar}>
            <i className='bi bi-x-lg' />
          </button>
        </div>
        <div className='circ-modal-body'>
          <div className='circ-tabs mb-3'>
            {TIPOS.map((t) => (
              <button
                key={t.value}
                type='button'
                className={`circ-tab ${tipo === t.value ? 'circ-tab-ativa' : ''}`}
                onClick={() => setTipo(t.value)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {carregando && <div className='text-muted text-center py-3'>Carregando...</div>}

          {!carregando && templates.length === 0 && (
            <div className='circ-empty-state'>
              <i className='bi bi-file-earmark-text circ-empty-state-icon' />
              <div className='circ-empty-state-title'>Nenhum modelo encontrado</div>
              <div className='circ-empty-state-texto mb-2'>
                Os 45 modelos institucionais ainda não foram semeados neste ambiente.
              </div>
              <button type='button' className='btn btn-sm btn-primary' disabled={semeando} onClick={semearInstitucionais}>
                {semeando && <span className='spinner-border spinner-border-sm me-1' />}
                Semear Modelos Institucionais
              </button>
            </div>
          )}

          {!carregando && templates.length > 0 && !templateEmEdicao && (
            <div className='d-flex flex-column gap-2'>
              {templates.map((t) => (
                <div key={t.id} className='circ-contato-item' style={{ display: 'block' }}>
                  <div className='d-flex justify-content-between align-items-center'>
                    <div>
                      <strong style={{ fontSize: 13 }}>{t.nome}</strong>{' '}
                      <span className={`badge ms-1 ${t.institucional ? 'badge-light-secondary' : 'badge-light-primary'}`}>
                        {LABEL_ESCOPO[t.escopo]}
                      </span>
                    </div>
                    <div className='d-flex gap-1'>
                      <button type='button' className='btn btn-sm btn-light' onClick={() => setDuplicandoId(t.id)}>
                        <i className='bi bi-files me-1' /> Duplicar
                      </button>
                      {!t.institucional && (
                        <button type='button' className='btn btn-sm btn-light' onClick={() => abrirEdicao(t)}>
                          <i className='bi bi-pencil me-1' /> Editar
                        </button>
                      )}
                    </div>
                  </div>
                  {t.versaoVigente && (
                    <div className='text-muted mt-1' style={{ fontSize: 12 }}>{t.versaoVigente.assunto}</div>
                  )}

                  {duplicandoId === t.id && (
                    <div className='d-flex gap-2 mt-2'>
                      <input
                        className='circ-form-control'
                        placeholder='Nome do novo modelo...'
                        value={nomeDuplicata}
                        onChange={(e) => setNomeDuplicata(e.target.value)}
                        autoFocus
                      />
                      <button type='button' className='btn btn-sm btn-primary' disabled={!nomeDuplicata.trim()} onClick={() => confirmarDuplicar(t)}>
                        Confirmar
                      </button>
                      <button type='button' className='btn btn-sm btn-light' onClick={() => { setDuplicandoId(undefined); setNomeDuplicata(''); }}>
                        Cancelar
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {templateEmEdicao && (
            <div>
              <div className='circ-form-group'>
                <label>Assunto</label>
                <input className='circ-form-control' value={assuntoEdicao} onChange={(e) => setAssuntoEdicao(e.target.value)} />
              </div>
              <div className='circ-form-group'>
                <label>Corpo (HTML)</label>
                <textarea className='circ-form-control' rows={8} value={corpoEdicao} onChange={(e) => setCorpoEdicao(e.target.value)} />
              </div>
              <div className='d-flex gap-2'>
                <button type='button' className='btn btn-sm btn-light' onClick={() => setTemplateEmEdicao(undefined)}>
                  Cancelar
                </button>
                <button type='button' className='btn btn-sm btn-primary' disabled={salvando} onClick={salvarEdicao}>
                  {salvando && <span className='spinner-border spinner-border-sm me-1' />}
                  Salvar Modelo
                </button>
              </div>
            </div>
          )}

          {mensagem && <div className='mt-3' style={{ fontSize: 12.5, color: '#47586b' }}>{mensagem}</div>}
        </div>
        <div className='circ-modal-footer'>
          <button type='button' className='btn btn-sm btn-light' onClick={onFechar}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplatesModal;
